import { Fragment, startTransition, useEffect, useMemo, useState } from "react"

import { Caption } from "@bounce/design-system"
import { useTranslation } from "@bounce/i18n"

import { StoreCapacityIndicatorDot } from "./StoreCapacityIndicatorDot"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"

type StoreCardCapacityCaptionProps = Pick<NearbyStorageSpot, "maxCapacity">

export const StoreCardCapacityCaption = ({ maxCapacity }: StoreCardCapacityCaptionProps) => {
  const { t } = useTranslation()

  const capacity = maxCapacity ?? -1

  const lowCapacityCopy = t("cmp.storeCard.capacity.almostFull", "Almost full!")
  const highCapacityCopy = t("cmp.storeCard.capacity.spotsLeft", "{capacity} spots left", { capacity })

  const hasHighCapacity = capacity >= 5

  return (
    <span className="flex flex-row items-center text-neutral-800">
      <StoreCapacityIndicatorDot color={hasHighCapacity ? "green" : "yellow"} className="mr-1" />
      <Caption>{hasHighCapacity ? highCapacityCopy : lowCapacityCopy}</Caption>
    </span>
  )
}

export type LiveStoreCardCapacityCaptionProps = {
  maxCapacity: number | null
  last7dReservationCount: number | undefined
}

export const LiveStoreCardCapacityCaption = ({
  maxCapacity,
  last7dReservationCount,
}: LiveStoreCardCapacityCaptionProps) => {
  const [showCapacityCaption, setShowCapacityCaption] = useState(false)

  const currentCapacity = useMemo(() => {
    // More realistic defaults based on store activity
    const getRealisticCapacity = (reservationCount: number) => {
      if (reservationCount >= 50) return 150 // High-traffic store
      if (reservationCount >= 20) return 100 // Medium-traffic store
      if (reservationCount >= 5) return 60 // Low-traffic store
      return 40 // Very low-traffic store
    }

    const baseCapacity = maxCapacity ?? getRealisticCapacity(last7dReservationCount ?? 0)
    const dailyAverage = (last7dReservationCount ?? 0) / 7

    // Smarter occupancy estimation
    const occupancyRate = Math.min(0.4, dailyAverage / baseCapacity) // Max 40% occupancy
    const currentOccupancy = Math.round(baseCapacity * occupancyRate)

    return Math.max(
      Math.round(baseCapacity * 0.2), // Never go below 20%
      baseCapacity - currentOccupancy,
    )
  }, [maxCapacity, last7dReservationCount])

  useEffect(() => {
    const randomDelay = Math.round(Math.random() * 1000 + 500) // 500ms to 1500ms
    const timer = setTimeout(() => {
      startTransition(() => {
        setShowCapacityCaption(true)
      })
    }, randomDelay)

    return () => clearTimeout(timer)
  }, [maxCapacity])

  return (
    <Fragment>
      {showCapacityCaption ? (
        <StoreCardCapacityCaption maxCapacity={currentCapacity} />
      ) : (
        <div className="flex h-4 w-16 animate-pulse items-center self-center rounded-xl bg-gray-200 md:h-5 md:w-20" />
      )}
    </Fragment>
  )
}
